// GIF picker (KLIPY). Searches run from the browser, results are shown in the order KLIPY returns them, and
// the panel carries KLIPY's attribution, as their terms require.
import { isNarrow } from "../mobile.ts";
import { Sheet } from "./Sheet.tsx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { searchGifs, type GifAttachment, type GifResult } from "../gifs.ts";
import { configFailureText, getConfig } from "../upload.ts";
import { dropKeyboard } from "../mobile.ts";
import { GifGrid } from "./GifGrid.tsx";

export function GifPicker({ customerId, onPick, onClose, embedded, initialQuery = "" }: { customerId: string; onPick: (gif: GifAttachment) => void; onClose: () => void; /** Inside MediaPicker's tabs: it places, closes and sheets the panel. */ embedded?: boolean; /** The search box starts with these words and the results load for them ("/gif cats", #632). */ initialQuery?: string }) {
  // undefined: asking /api/config. null: it loaded and has no KLIPY key ("not set up"). `failure`: it did not load.
  const [key, setKey] = useState<string | null | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState<GifResult[]>([]);
  // A new search is a new grid: its loader starts clean (#736). Another page of the same search keeps the grid.
  const [gen, setGen] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  // Counts the searches: a "More GIFs" page that was asked for before a new search began belongs to the old query and is dropped.
  const searches = useRef(0);

  const loadKey = () => {
    setFailure(null);
    setKey(undefined);
    void getConfig().then((c) => setKey(c.klipyKey ?? null), (e) => setFailure(configFailureText(e)));
  };
  useEffect(() => {
    loadKey();
    if (embedded) return;
    const onDown = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Search as you type (debounced); empty query shows trending.
  useEffect(() => {
    searches.current++;
    if (!key) return;
    let stale = false;
    const t = setTimeout(async () => {
      setBusy(true);
      setError(null);
      try {
        const r = await searchGifs(key, q.trim(), 1, customerId);
        if (stale) return;
        setResults(r.results);
        setGen((g) => g + 1);
        setHasNext(r.hasNext);
        setPage(1);
      } catch (e) {
        if (!stale) setError((e as Error).message);
      } finally {
        if (!stale) setBusy(false);
      }
    }, q ? 300 : 0);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [key, q]);

  const more = async () => {
    if (!key || busy) return;
    const search = searches.current;
    setBusy(true);
    try {
      const r = await searchGifs(key, q.trim(), page + 1, customerId);
      if (search !== searches.current) return;
      setResults((prev) => [...prev, ...r.results]);
      setHasNext(r.hasNext);
      setPage(page + 1);
    } catch (e) {
      if (search === searches.current) setError((e as Error).message);
    } finally {
      if (search === searches.current) setBusy(false);
    }
  };

  const sheet = isNarrow();
  const search = useRef<HTMLInputElement>(null);
  // Into the search box as soon as it opens (and again once the key arrives: a disabled box can't take focus).
  useLayoutEffect(() => {
    if (!sheet) search.current?.focus({ preventScroll: true });
  }, [!!key]);
  const panel = (
    <div className={`gif-picker${sheet ? " in-sheet" : ""}${embedded ? " embedded" : ""}`} ref={box} role={embedded ? "tabpanel" : "dialog"} aria-label="GIFs">
      <div className="emoji-search">
        <label htmlFor="gif-q" className="sr-only">Search GIFs</label>
        <input id="gif-q" ref={search} className="input" placeholder="Search KLIPY" value={q} onChange={(e) => setQ(e.target.value)} disabled={!key} />
      </div>
      <div
        className="gif-grid-wrap sheet-scroll"
        onTouchStart={dropKeyboard}
        onScroll={(e) => {
          // Scrolling on loads the next page before the end (the button stays for keyboards and slow networks).
          const el = e.currentTarget;
          if (hasNext && !busy && el.scrollHeight - el.scrollTop - el.clientHeight < 400) void more();
        }}
      >
        {((key === undefined && !failure) || (busy && results.length === 0)) && <p className="muted" style={{ padding: 12, fontSize: 13 }}>Loading GIFs…</p>}
        {failure && (
          <div style={{ padding: 12, fontSize: 13 }}>
            <p className="error">GIFs didn't load: {failure}</p>
            <button type="button" className="btn btn-small" onClick={loadKey}>Try again</button>
          </div>
        )}
        {key === null && !failure && <p className="muted" style={{ padding: 12, fontSize: 13 }}>GIFs aren't set up yet: the server needs a KLIPY key.</p>}
        {error && <p className="error" style={{ padding: 12, fontSize: 13 }}>{error}</p>}
        {key && !error && !busy && results.length === 0 && <p className="muted" style={{ padding: 12, fontSize: 13 }}>No GIFs found.</p>}
        <GifGrid key={gen} results={results} onPick={onPick} />
        {hasNext && (
          <div style={{ textAlign: "center", padding: 8 }}>
            <button className="btn btn-small" onClick={more} disabled={busy}>{busy ? "Loading…" : "More GIFs"}</button>
          </div>
        )}
      </div>
      <div className="gif-attribution">Powered by KLIPY</div>
    </div>
  );
  if (embedded) return panel;
  // Phones: a bottom sheet instead of a floating panel.
  return sheet ? (
    <Sheet label="GIFs" onClose={onClose}>
      {panel}
    </Sheet>
  ) : (
    panel
  );
}
